import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Calendar, Users, ExternalLink, X, Video, Wrench } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";
import TagsInput from "@/components/TagsInput";

const EMPTY = {
  title: "", description: "", type: "webinar", host_name: "",
  scheduled_at: "", duration_min: 60, meet_url: "", registration_url: "",
  cover_image_url: "", tags: [], capacity: null, is_free: true, price_npr: 0,
  prerequisites: "", agenda: "",
};

export default function AdminEventsPage() {
  const [events, setEvents] = useState([]);
  const pager = usePager(events, 15);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/events", { params: { state: "all" } });
    setEvents(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ ...EMPTY, scheduled_at: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 16) });
  };

  const startEdit = (e) => {
    setEditing(e.id);
    setForm({
      ...EMPTY, ...e,
      scheduled_at: e.scheduled_at ? new Date(e.scheduled_at).toISOString().slice(0, 16) : "",
      tags: e.tags || [],
    });
  };

  const save = async () => {
    if (!form.title || !form.scheduled_at) { toast.error("Title and date are required"); return; }
    const payload = { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() };
    try {
      if (editing === "new") await api.post("/admin/events", payload);
      else await api.put(`/admin/events/${editing}`, payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-events-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">WEBINARS & WORKSHOPS</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Run <em className="italic text-kb-primary">research events</em></h1>
        </div>
        <button onClick={startNew} className="kb-btn-primary text-sm" data-testid="event-new-btn">
          <Plus className="w-3.5 h-3.5" /> New event
        </button>
      </div>

      {editing && (
        <div className="mt-6 kb-card p-6" data-testid="event-editor">
          <div className="flex items-center justify-between mb-4">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">{editing === "new" ? "NEW EVENT" : "EDITING"}</div>
            <button onClick={() => setEditing(null)} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="kb-input text-sm sm:col-span-2" placeholder="Event title"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="event-title-input" />
            <textarea rows={3} className="kb-input resize-none text-sm sm:col-span-2" placeholder="Description"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <select className="kb-input text-sm" value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="event-type-select">
              <option value="webinar">Webinar</option>
              <option value="workshop">Workshop</option>
            </select>
            <input type="datetime-local" className="kb-input text-sm" value={form.scheduled_at}
              onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
              data-testid="event-date-input" />
            <input className="kb-input text-sm" placeholder="Host name"
              value={form.host_name} onChange={(e) => setForm({ ...form, host_name: e.target.value })} />
            <input type="number" className="kb-input text-sm" placeholder="Duration (min)" value={form.duration_min}
              onChange={(e) => setForm({ ...form, duration_min: (e.target.value === "" ? "" : (parseInt(e.target.value, 10) || 60)) })} />
            <input className="kb-input text-sm sm:col-span-2" placeholder="Google Meet / Zoom URL"
              value={form.meet_url} onChange={(e) => setForm({ ...form, meet_url: e.target.value })} />
            <input className="kb-input text-sm sm:col-span-2" placeholder="Cover image URL (optional)"
              value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} />
            <div className="sm:col-span-2">
              <TagsInput
                value={form.tags || []}
                onChange={(tags) => setForm({ ...form, tags })}
                placeholder="Tags (type and press Enter)"
                testId="event-tags"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-kb-ink-muted">
              <input type="checkbox" checked={form.is_free}
                onChange={(e) => setForm({ ...form, is_free: e.target.checked, price_npr: e.target.checked ? 0 : form.price_npr })} />
              Free to attend
            </label>
            {!form.is_free && (
              <input type="number" className="kb-input text-sm" placeholder="Price NPR" value={form.price_npr}
                onChange={(e) => setForm({ ...form, price_npr: (e.target.value === "" ? "" : (parseFloat(e.target.value) || 0)) })} />
            )}
            <input type="number" className="kb-input text-sm" placeholder="Capacity (optional)" value={form.capacity || ""}
              onChange={(e) => setForm({ ...form, capacity: e.target.value ? parseInt(e.target.value) : null })} />
            <textarea rows={3} className="kb-input text-sm sm:col-span-2 resize-none"
              placeholder="Agenda (optional)" value={form.agenda}
              onChange={(e) => setForm({ ...form, agenda: e.target.value })}
              data-testid="event-agenda-input" />
            <textarea rows={2} className="kb-input text-sm sm:col-span-2 resize-none"
              placeholder="Prerequisites (optional)" value={form.prerequisites}
              onChange={(e) => setForm({ ...form, prerequisites: e.target.value })}
              data-testid="event-prereqs-input" />
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} className="kb-btn-primary text-sm" data-testid="event-save-btn">Save event</button>
            <button onClick={() => setEditing(null)} className="kb-btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        events.length === 0 ? <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No events scheduled.</div> :
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {pager.pageItems.map((e) => {
            const state = e.state || (new Date(e.scheduled_at) > new Date() ? "scheduled" : "completed");
            const stateLabel = state === "ongoing" ? "LIVE NOW" : state === "scheduled" ? "UPCOMING" : "PAST";
            const stateColor = state === "ongoing" ? "text-rose-700" : state === "scheduled" ? "text-emerald-700" : "text-kb-ink-light";
            return (
              <div key={e.id} className="kb-card p-5" data-testid={`event-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {e.type === "workshop"
                      ? <Wrench className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                      : <Video className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />}
                    <div className="font-serif text-lg text-kb-ink mt-2 line-clamp-1">{e.title}</div>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
                      <Calendar className="w-3 h-3 inline mr-1" />{new Date(e.scheduled_at).toLocaleString()} · {e.duration_min}m
                    </div>
                    {e.host_name && <div className="text-xs text-kb-ink-muted mt-1">Host: {e.host_name}</div>}
                    <div className="kb-mono text-[10px] uppercase tracking-wider mt-2 flex gap-2 items-center flex-wrap">
                      <span className={stateColor}>{stateLabel}</span>
                      <span className="text-kb-ink-muted"><Users className="w-3 h-3 inline" /> {e.registrations || 0}</span>
                      {e.is_free ? <span className="text-emerald-700">FREE</span> : <span className="text-kb-primary">NPR {e.price_npr?.toLocaleString()}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => startEdit(e)} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" data-testid={`event-${e.id}-edit`}><Edit3 className="w-3.5 h-3.5" /></button>
                    <button
                      onClick={async () => {
                        try {
                          const r = await api.post(`/admin/events/${e.id}/attach-meet`);
                          toast.success("Google Meet attached");
                          load();
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Connect Google first (Settings → Connect Google)");
                        }
                      }}
                      className="p-1.5 text-kb-ink-muted hover:text-kb-primary"
                      data-testid={`event-${e.id}-meet`}
                      title="Create Google Meet link"
                    >
                      <Video className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(e.id)} className="p-1.5 text-rose-600 hover:text-rose-700" data-testid={`event-${e.id}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {e.meet_url && (
                  <a href={e.meet_url} target="_blank" rel="noreferrer" className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1 mt-3">
                    Join link <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            );
          })}
        </div>}
      <Pager {...pager} label="events" testId="admin-events-pager" />
    </div>
  );
}